import { useBackendFeatureFlags } from '../context/backend-feature-flags-context';

export type FeatureFlags = {
  isDemoEnv: boolean;
  showTimeTracking: boolean;
  showNotifications: boolean;
  showTimeOffs: boolean;
  showOverheads: boolean;
  showTimeOffsOnDemo: boolean;
  showTimeOffsOnProd: boolean;
  usePlannerCachePatching: boolean;
  useFcmForFinanceLists: boolean;
  showPaymentDetails: boolean;
  /** Controlled by the backend REPORTS feature flag, not an env var. */
  showReports: boolean;
};

export type FeatureFlagKey = keyof FeatureFlags;

const ENV_FEATURE_FLAGS: FeatureFlags = {
  isDemoEnv: import.meta.env.VITE_IS_DEMO_ENV === 'true',
  showTimeTracking: import.meta.env.VITE_SHOW_TIME_TRACKING === 'true',
  showNotifications: import.meta.env.VITE_SHOW_NOTIFICATIONS === 'true',
  showTimeOffs: import.meta.env.VITE_SHOW_TIME_OFFS === 'true',
  /** Project Overheads (Finance → Overheads). Frontend-only flag while the feature runs on a mocked API. */
  showOverheads: import.meta.env.VITE_SHOW_OVERHEADS === 'true',
  showTimeOffsOnDemo: import.meta.env.VITE_SHOW_TIME_OFFS_ON_DEMO === 'true',
  showTimeOffsOnProd: import.meta.env.VITE_SHOW_TIME_OFFS_ON_PROD === 'true',
  /** When true, planner uses client-side cache patching after assignment mutations (faster, no extra API calls).
   *  When false, falls back to backend invalidation (refetches all pages, slower but always accurate). */
  usePlannerCachePatching: import.meta.env.VITE_USE_PLANNER_CACHE_PATCHING !== 'false',
  /** When true, the invoices/bills lists refresh via FCM push (with long-polling as a fallback when
   *  notifications are unavailable). When false (default), FCM is skipped entirely and the lists always
   *  use long-polling. Disabled by default while backend FCM delivery is unreliable (OPS-1560 / OPS-1449);
   *  flip to true to re-enable the FCM path once the backend is fixed. */
  useFcmForFinanceLists: import.meta.env.VITE_USE_FCM_FOR_FINANCE_LISTS === 'true',
  /** Payment Details (OPS-1610): payment-details library on the Finance settings page and the
   *  per-invoice selector on the Generate Invoices screen. */
  showPaymentDetails: import.meta.env.VITE_SHOW_PAYMENT_DETAILS === 'true',
  showReports: false, // always off from env; driven by backend REPORTS flag
};

/**
 * Hook to access feature flags. Env-var flags are available immediately;
 * backend-driven flags (showReports, and the backend half of showTimeOffs)
 * resolve after the first API call.
 */
export const useFeatureFlags = (): FeatureFlags => {
  const backendFlags = useBackendFeatureFlags();
  return {
    ...ENV_FEATURE_FLAGS,
    showReports: backendFlags.has('REPORTS'),
    // Enabled via either the backend TIME_OFFS flag (per-org/user override) or the local env var.
    showTimeOffs: ENV_FEATURE_FLAGS.showTimeOffs || backendFlags.has('TIME_OFFS'),
  };
};

/**
 * Returns a stable callback that checks whether a feature flag is enabled.
 * Use this inside components that need to check flags in permission evaluations.
 */
export const useIsFeatureEnabled = (): ((flag: FeatureFlagKey) => boolean) => {
  const flags = useFeatureFlags();
  return (flag: FeatureFlagKey) => flags[flag];
};

/**
 * Utility to check a specific env-var feature flag outside of React components.
 * Does NOT reflect backend-driven flags (e.g. showReports, or the backend half of
 * showTimeOffs). Prefer useIsFeatureEnabled inside components.
 */
export const isFeatureEnabled = (flag: FeatureFlagKey): boolean => {
  return ENV_FEATURE_FLAGS[flag];
};
